import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconArrowUp } from '../../../base/icons/svg';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { getEffectiveRosterPermissions } from '../../../base/permissions';
import { getBrandingColorPalette } from '../../../base/styles';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import ListItem from '../../../base/ui/components/web/ListItem';
import { getFocusModeVisibleRoomParticipants } from '../../../focus-mode/functions';
import { ParticipantItem } from '../../../participants-pane/components/ParticipantItem';
import { ACTION_TRIGGER } from '../../../participants-pane/constants';
import { participantMatchesSearch } from '../../../participants-pane/functions';
import { showOverflowDrawer } from '../../../toolbox/functions.web';


const useStyles = makeStyles()(theme => {
    return {
        container: {
            boxShadow: 'none'
        },

        roomName: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...withPixelLineHeight(theme.typography.bodyLongBold)
        },

        arrowContainer: {
            backgroundColor: theme.palette.ui03,
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            marginRight: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none'
        }
    };
});

export const CollapsibleRoom = ({
    actionsTrigger = ACTION_TRIGGER.HOVER,
    children,
    isHighlighted,
    onRaiseMenu,
    onLeave,
    participantContextEntity,
    raiseParticipantContextMenu,
    room,
    searchString,
    toggleParticipantMenu
}) => {
    const { t } = useTranslation();
    const { classes: styles, cx } = useStyles();
    const [ collapsed, setCollapsed ] = useState(false);
    const toggleCollapsed = useCallback(() => {
        setCollapsed(!collapsed);
    }, [ collapsed ]);
    const interfaceConfig = useSelector(state => state['features/base/config'].interfaceConfig);
    const raiseMenu = useCallback(target => {
        onRaiseMenu(target);
    }, [ onRaiseMenu ]);
    const { defaultRemoteDisplayName } = useSelector(state => state['features/base/config']);
    const overflowDrawer = useSelector(showOverflowDrawer);
    const moderator = useSelector(isLocalParticipantModerator);

    // Focus Mode: a restricted guest may only see the hosts of another room,
    // never the other guests in it.
    const roomParticipants = useSelector(state => getFocusModeVisibleRoomParticipants(state, room));
    const roomParticipantCount = roomParticipants.length;
    const arrowContainerStyle = {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        marginRight: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        backgroundColor: getBrandingColorPalette(interfaceConfig)?.accent1
    };

    const arrow = (<button
        aria-label = { collapsed ? t('breakoutRooms.hideParticipantList', 'Hide participant list')
            : t('breakoutRooms.showParticipantList', 'Show participant list')
        }
        className = ' breakout-room-arrow-container'
        style = { arrowContainerStyle }>
        <Icon
            fillColor = { getBrandingColorPalette(interfaceConfig)?.primary }
            size = { 12 }
            src = { collapsed ? IconArrowDown : IconArrowUp } />
    </button>);

    const roomName = (<span className = { styles.roomName }>
        {`${room.name || t('breakoutRooms.mainRoom')} (${roomParticipantCount})`}
    </span>);

    const raiseParticipantMenu = useCallback(({ participantID, displayName }) => moderator
    && raiseParticipantContextMenu({
        room,
        jid: participantID,
        participantName: displayName
    }), [ room, moderator ]);

    return (<>
        <ListItem
            actions = { children }
            className = { cx(styles.container, 'breakout-room-container') }
            defaultName = { `${room.name || t('breakoutRooms.mainRoom')} (${roomParticipantCount})` }
            icon = { arrow }
            isHighlighted = { isHighlighted }
            onClick = { toggleCollapsed }
            onLongPress = { raiseMenu }
            onMouseLeave = { onLeave }
            testId = { room.id }
            textChildren = { roomName }
            trigger = { actionsTrigger } />
        {!collapsed && roomParticipants.map(p => participantMatchesSearch(p, searchString) && (
            <ParticipantItem
                actionsTrigger = { ACTION_TRIGGER.HOVER }
                ariaLabel = { t('breakoutRooms.actions.more') }
                hideOptions = { !moderator }
                isHeld = { p.held === true }
                isHighlighted = { participantContextEntity?.jid === p.jid }
                isHost = { p.host === true }
                isModerator = { p.role === 'moderator' }
                key = { p.jid }
                local = { false }
                name = { p.displayName || defaultRemoteDisplayName }
                onContextMenu = { toggleParticipantMenu({ room,
                    jid: p.jid,
                    participantName: p.displayName }) }
                openDrawerForParticipant = { raiseParticipantMenu }
                overflowDrawer = { overflowDrawer }
                participantID = { p.jid }
                permissions = { getEffectiveRosterPermissions(p.webinarRole || p.role) }
                role = { p.webinarRole || p.role } />
        ))
        }
    </>);
};
